import React from 'react';
import NavigationPrompt from 'react-router-navigation-prompt';

import FigoLoadingOverlay from '../../common/FigoLoadingOverlay';
import {
  AmountInput,
  AttachmentSelector,
  CheckAddressLabel,
  ClaimbotStepper,
  ClaimSelector,
  ClaimSubmitted,
  ClaimSummary,
  ConditionsSelector,
  DateInput,
  FileClaimbotEvie,
  FinishButton,
  MultipleOptionsSelector,
  PetSelector,
  PolicySelector,
  ReimbursementMethodOptionsSelector,
  SignAndSubmit,
  TermsAndConditionCareCredit,
  TermsAndConditionPPU,
  TextInput,
  TextInputSingleLine,
  TwoOptionsSelector,
} from './components';
import FigoLottie from '../../common/FigoLottie';
import UnsavedChangesPrompt from '../../common/UnsavedChangesPrompt';
import { ContactUsModal } from '../../sideMenu/contactUs/ContactUsModal';
import { CONTACT_US_STEPS } from '../../sideMenu/contactUs/contactUs.constants';

import { useFileClaimbotScreen } from './hooks/useFileClaimbotScreen';

import './FileClaimbotScreen.css';

const FileClaimbotScreen = () => {
  const {
    accountNumber,
    accountNumberVisible,
    agentImage,
    agentText,
    amountInput,
    amountInputVisible,
    attachments,
    attSelectorVisible,
    backButtonLabel,
    checkAddressLegendVisible,
    claimbotNavPromptVisible,
    claimSelectorVisible,
    claimSubmittedVisible,
    claimSummaryInfo,
    claimSummaryVisible,
    claimTypes,
    claimTypeSelected,
    conditions,
    conditionsCatalog,
    conditionsSelectorVisible,
    contactUsVisible,
    currentStep,
    currentStepNumber,
    dateInputVisible,
    fileLimit,
    finishButtonVisible,
    genericError,
    genericErrorVisible,
    goBackDisabled,
    goForwardisabled,
    loadingAdditionalSteps,
    loadingAllBrandsBillingPaymentsData,
    loadingConditions,
    loadingPolicyByDate,
    loadingSteps,
    marketingChannelId,
    maxAttachmentSizeReached,
    maxCoverageDate,
    minCoverageDate,
    multiOptSelectVisible,
    multipleOptions,
    nextButtonLabel,
    numberOfSteps,
    onAccountNumberChanged,
    onAddAttachment,
    onAgreePolicyHolderChange,
    onAmountInputChanged,
    onConditionRemoved,
    onConditionsSelected,
    onNextStepClick,
    onOneOptionSelected,
    onPetItemPress,
    onPolicyItemPress,
    onPreviousStepClick,
    onRemoveAttachment,
    onRoutingNumberChanged,
    onSelectClaimType,
    onSelectedValidDate,
    onSignatureTextChanged,
    onSignatureTypeSelection,
    onTermsAndCondCareCreditChange,
    onTermsOfUseChange,
    onTextInputChanged,
    optionSelected,
    petPolicies,
    pets,
    petSelectorVisible,
    policyBrandName,
    policySelected,
    policySelectorVisible,
    reimbMethodOptSelectVisible,
    routingNumber,
    routingNumberVisible,
    selectedDate,
    selectedPet,
    signAndSubmitVisible,
    signature,
    signatureText,
    signatureTypeSelected,
    submitClaim,
    t,
    termsAndCondCareCreditSelected,
    termsAndCondCareCreditVisible,
    termsAndCondPPUSelected,
    termsAndCondPPUVisible,
    textInputSingleLineVisible,
    textInputValue,
    textInputVisible,
    toggleContactUsModalVisible,
    twoOptions,
    twoOptSelectVisible,
    unknownStepVisible,
  } = useFileClaimbotScreen();

  return (
    <div className="File-claimbot-main-container">
      <NavigationPrompt when={claimbotNavPromptVisible}>
        {({ onConfirm, onCancel }) => (
          <UnsavedChangesPrompt
            containerClassName="File-claim-type-container"
            currentStepNumber={currentStepNumber}
            icon={null}
            message={t('unsaveClaimModal.content')}
            modalClassName="File-claim-unsave-modal"
            onCancel={onConfirm}
            onConfirm={onCancel}
            policyBrandName={policyBrandName}
            textCancel={t('unsaveClaimModal.cancelTextButton')}
            textConfirm={t('unsaveClaimModal.confirmTextButton')}
            title={t('unsaveClaimModal.title')}
          />
        )}
      </NavigationPrompt>

      <FigoLoadingOverlay visible={loadingSteps} />

      <div className="Screen-header-claimbot">
        <h1>{t('title')}</h1>
      </div>

      <div className="File-claimbot-content">
        <ClaimbotStepper
          backButtonLabel={backButtonLabel}
          currentStepName={t('stepTitle')}
          currentStepNumber={currentStepNumber}
          goBackDisabled={goBackDisabled}
          goForwardisabled={goForwardisabled}
          loading={submitClaim.loading || loadingAllBrandsBillingPaymentsData
            || loadingAdditionalSteps || loadingPolicyByDate}
          nextButtonLabel={nextButtonLabel}
          numberOfSteps={numberOfSteps}
          onNextStepClick={onNextStepClick}
          onPreviousStepClick={onPreviousStepClick}
          submitted={submitClaim.submitted}
        />

        <div className="card File-card-container">
          <div className="File-claim-card-body">
            <FileClaimbotEvie
              agentImage={agentImage}
              agentText={agentText}
              loading={loadingSteps}
            />

            <div className="File-claimbot-card-content">
              {finishButtonVisible ? <FinishButton /> : null}

              {petSelectorVisible ? (
                <PetSelector
                  onPetItemPress={onPetItemPress}
                  pets={pets}
                  selectedPet={selectedPet}
                />
              ) : null}

              {policySelectorVisible ? (
                <PolicySelector
                  onContactSupportClick={toggleContactUsModalVisible}
                  onPolicyItemPress={onPolicyItemPress}
                  petPoliciesDisabled={petPolicies.disabled}
                  petPoliciesEnabled={petPolicies.enabled}
                  selectedPolicy={policySelected}
                />
              ) : null}

              {dateInputVisible ? (
                <DateInput
                  maxDate={maxCoverageDate}
                  minDate={minCoverageDate}
                  onSelectedValidDate={onSelectedValidDate}
                  selectedDate={selectedDate}
                />
              ) : null}

              {claimSelectorVisible ? (
                <ClaimSelector
                  claimTypes={claimTypes}
                  claimTypeSelected={claimTypeSelected}
                  onSelectClaimType={onSelectClaimType}
                />
              ) : null}

              {textInputVisible ? (
                <TextInput
                  onTextInputChanged={onTextInputChanged}
                  textInputValue={textInputValue}
                />
              ) : null}

              {textInputSingleLineVisible ? (
                <TextInputSingleLine
                  onTextInputChanged={onTextInputChanged}
                  textInputValue={textInputValue}
                />
              ) : null}

              {attSelectorVisible ? (
                <AttachmentSelector
                  attachments={attachments}
                  fileLimit={fileLimit}
                  maxAttachmentSizeReached={maxAttachmentSizeReached}
                  onAddAttachment={onAddAttachment}
                  onRemoveAttachment={onRemoveAttachment}
                />
              ) : null}

              {amountInputVisible ? (
                <AmountInput
                  number={amountInput}
                  onChange={onAmountInputChanged}
                  placeholder={t('amountInput.placeholder')}
                  title={t('amountInput.title')}
                  type="text"
                  useCurrencyFormat
                />
              ) : null}

              {routingNumberVisible ? (
                <AmountInput
                  number={routingNumber}
                  onChange={onRoutingNumberChanged}
                  placeholder={t('routingNumberInput.placeholder')}
                  title={t('routingNumberInput.title')}
                />
              ) : null}

              {accountNumberVisible ? (
                <AmountInput
                  number={accountNumber}
                  onChange={onAccountNumberChanged}
                  placeholder={t('accountNumberInput.placeholder')}
                  title={t('accountNumberInput.title')}
                />
              ) : null}

              {twoOptSelectVisible ? (
                <TwoOptionsSelector
                  onOneOptionSelected={onOneOptionSelected}
                  optionSelected={optionSelected}
                  twoOptions={twoOptions}
                />
              ) : null}

              {reimbMethodOptSelectVisible ? (
                <ReimbursementMethodOptionsSelector
                  onOneOptionSelected={onOneOptionSelected}
                  optionSelected={optionSelected}
                  twoOptions={twoOptions}
                />
              ) : null}

              {checkAddressLegendVisible ? (
                <CheckAddressLabel />
              ) : null}

              {multiOptSelectVisible ? (
                <MultipleOptionsSelector
                  multipleOptions={multipleOptions}
                  onOneOptionSelected={onOneOptionSelected}
                  optionSelected={optionSelected}
                />
              ) : null}

              {conditionsSelectorVisible ? (
                <ConditionsSelector
                  conditions={conditions}
                  conditionsCatalog={conditionsCatalog}
                  loading={loadingConditions}
                  marketingChannelId={marketingChannelId}
                  maxDate={maxCoverageDate}
                  minDate={minCoverageDate}
                  onConditionRemoved={onConditionRemoved}
                  onConditionsSelected={onConditionsSelected}
                />
              ) : null}

              {termsAndCondPPUVisible ? (
                <TermsAndConditionPPU
                  marketingChannelId={marketingChannelId}
                  onAgreePolicyHolderChange={onAgreePolicyHolderChange}
                  onTermsOfUseChange={onTermsOfUseChange}
                  termsAndCondPPUSelected={termsAndCondPPUSelected}
                />
              ) : null}

              {termsAndCondCareCreditVisible ? (
                <TermsAndConditionCareCredit
                  onChange={onTermsAndCondCareCreditChange}
                  selected={termsAndCondCareCreditSelected}
                />
              ) : null}

              {claimSummaryVisible ? (
                <ClaimSummary
                  claimSummaryInfo={claimSummaryInfo}
                  selectedPet={selectedPet}
                />
              ) : null}

              {signAndSubmitVisible ? (
                <SignAndSubmit
                  onSignatureTextChanged={onSignatureTextChanged}
                  onSignatureTypeSelection={onSignatureTypeSelection}
                  signature={signature}
                  signatureText={signatureText}
                  signatureTypeSelected={signatureTypeSelected}
                />
              ) : null}

              {submitClaim.loading || loadingAdditionalSteps
                || loadingPolicyByDate ? <FigoLottie /> : null}

              {claimSubmittedVisible ? (
                <ClaimSubmitted
                  policyBrandName={policyBrandName}
                  selectedPolicy={policySelected}
                  success={submitClaim.success}
                  toggleContactUsModalVisible={toggleContactUsModalVisible}
                />
              ) : null}

              <ContactUsModal
                companyStep={CONTACT_US_STEPS.HOME}
                onClose={toggleContactUsModalVisible}
                show={contactUsVisible}
              />
            </div>

            {unknownStepVisible
              ? (<span>UNKNOWN STEP {currentStep.KeyName}</span>) : null}

            {genericErrorVisible ? <span>{genericError}</span> : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export { FileClaimbotScreen };
